import React, { useEffect, useRef, useCallback } from "react";

import { observer } from "mobx-react";

import { PreviewElementItem } from "../packages/dp-result-viewer/preview-viewer/preview-element-item";
import { parsingLAStore } from "../utils/mobx/ParsingLAStore";
import { resultInteractionStore } from "../utils/mobx/ResultInteractionStore";

const ParsingResultLAPreview = () => {
  const { laElements: elements } = parsingLAStore;

  const { hoveredBoxIds, setHoveredBoxIds, selectedBoxIds, setSelectedBoxIds } =
    resultInteractionStore;

  const selectedElementRefs = useRef<Record<string, HTMLElement>>({});

  const handleMouseEnter = useCallback(
    (id: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!hoveredBoxIds.includes(id)) {
        setHoveredBoxIds([id]);
      }
    },
    [hoveredBoxIds, setHoveredBoxIds],
  );

  const handleMouseLeave = useCallback(
    () => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hoveredBoxIds.length > 0) {
        setHoveredBoxIds([]);
      }
    },
    [hoveredBoxIds, setHoveredBoxIds],
  );

  const handleClick = useCallback(
    (id: string) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedBoxIds([id]);
    },
    [setSelectedBoxIds],
  );

  useEffect(() => {
    if (selectedBoxIds.length === 0) return;
    const focusId = selectedBoxIds[0];
    if (focusId in selectedElementRefs.current) {
      const el = selectedElementRefs.current[focusId];
      el?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedBoxIds]);

  const setRef = useCallback(
    (elemId: string) => (ref: HTMLElement) => {
      selectedElementRefs.current[elemId] = ref;
    },
    [],
  );

  return (
    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
      <div className="space-y-1.5 rounded-lg bg-background p-4">
        <div className="mt-1 flex flex-col">
          {elements.map((element, index) => {
            const hasCoordinates = true;

            const highlightId = `page-${element.page}-element-${index}`;
            const isSelected = selectedBoxIds.includes(highlightId);
            const isHovered = hoveredBoxIds.includes(highlightId);

            const prevElement = index > 0 ? elements[index - 1] : null;
            const isNewPage =
              prevElement !== null && prevElement.page !== element.page;

            return (
              <div
                key={`element-wrapper-${index}`}
                id={`preview-element-${index}`}
                ref={
                  setRef(
                    highlightId,
                  ) as unknown as React.RefObject<HTMLDivElement>
                }
              >
                {isNewPage && <div className="page-separator" />}

                <PreviewElementItem
                  element={{
                    ...element,
                    content: {
                      html: element.html,
                      text: "",
                      markdown: "",
                    },
                  }}
                  index={index}
                  isSelected={isSelected}
                  isHovered={isHovered}
                  hasCoordinates={hasCoordinates}
                  onClick={handleClick(highlightId)}
                  onMouseEnter={handleMouseEnter(highlightId)}
                  onMouseLeave={handleMouseLeave()}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default observer(ParsingResultLAPreview);
